import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import { Integrations } from '@sentry/tracing'
import { ModalPlugin, ToastPlugin } from 'bootstrap-vue'
import VueCompositionAPI, { watch } from '@vue/composition-api'

import vueAwesomeCountdown from 'vue-awesome-countdown'
import i18n from '@/libs/i18n'
import ability from '@/libs/acl/ability'
import useJwt from '@/auth/jwt/useJwt'

// Global Components
import './global-components'

// 3rd party plugins
import '@axios'
import '@/libs/acl'
import '@/libs/portal-vue'
import '@/libs/clipboard'
import '@/libs/toastification'
import '@/libs/vue-select'
import '@/libs/tour'
import '@/libs/v-mask'

// Axios Mock Adapter
import '@/@fake-db/db'

// Settings
import settingsModule from '@/helpers/settings.module'
import setupRouteGuards from '@/router/guard'

import { isUserLoggedIn } from '@/auth/utils'

import App from './App.vue'
import store from './store'
import router from './router'

const isSentryEnabled = process.env.MIX_SENTRY_ENABLED === 'true'
const sentryDsn = process.env.MIX_SENTRY_DSN
const sentryEnvironment = process.env.MIX_SENTRY_ENVIRONMENT || process.env.MIX_APP_ENV || 'local'
const isSentryTracingEnabled = process.env.MIX_SENTRY_TRACING_ENABLED === 'true'
const isSentryReplayEnabled = process.env.MIX_SENTRY_REPLAY_ENABLED === 'true'
const sentryReplaySessionSampleRate = Number(process.env.MIX_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || 0.1)
const sentryReplayOnErrorSampleRate = Number(process.env.MIX_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || 1)

const resolveSentryTraceSampleRate = environment => {
  if (environment === 'production') {
    return Number(process.env.MIX_SENTRY_TRACES_SAMPLE_RATE_PRODUCTION || 0.2)
  }
  if (environment === 'staging') {
    return Number(process.env.MIX_SENTRY_TRACES_SAMPLE_RATE_STAGING || 0.7)
  }
  if (environment === 'local') {
    return Number(process.env.MIX_SENTRY_TRACES_SAMPLE_RATE_LOCAL || 0)
  }
  if (environment.startsWith('rv-')) {
    return Number(process.env.MIX_SENTRY_TRACES_SAMPLE_RATE_REVIEW || 0.2)
  }

  return Number(process.env.MIX_SENTRY_TRACES_SAMPLE_RATE_DEFAULT || 0.2)
}

if (isSentryEnabled && sentryDsn) {
  const integrations = []

  if (isSentryTracingEnabled) {
    const tracingOrigins = [/^\//]
    if (process.env.MIX_APP_URL) {
      tracingOrigins.push(process.env.MIX_APP_URL)
    }

    integrations.push(
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins,
      }),
    )
  }

  if (isSentryReplayEnabled) {
    integrations.push(new Sentry.Replay())
  }

  Sentry.init({
    Vue,
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: process.env.MIX_SENTRY_RELEASE || undefined,
    integrations,
    tracesSampleRate: isSentryTracingEnabled ? resolveSentryTraceSampleRate(sentryEnvironment) : 0,
    replaysSessionSampleRate: isSentryReplayEnabled ? sentryReplaySessionSampleRate : 0,
    replaysOnErrorSampleRate: isSentryReplayEnabled ? sentryReplayOnErrorSampleRate : 0,
  })
}

Vue.use(settingsModule)

// BSV Plugin Registration
Vue.use(ToastPlugin)
Vue.use(ModalPlugin)
// Vue.use(Toaster)
Vue.use(vueAwesomeCountdown)

// Composition API
Vue.use(VueCompositionAPI)

// Feather font icon - For form-wizard
// * Shall remove it if not using font-icons of feather-icons - For form-wizard
require('@core/assets/fonts/feather/iconfont.css') // For form-wizard

// import core styles
require('@core/scss/core.scss')

// import assets styles
require('@/assets/scss/style.scss')

Vue.config.productionTip = false

const abilityPromise = useJwt.axiosIns.get('/auth/ability')
Promise.all([abilityPromise])
  .then(values => {
    ability.update(values[0].data)

    // Adds guard with redirects etc
    setupRouteGuards(router)

    const app = new Vue({
      router,
      store,
      i18n,
      data: {
        isDisplayedPoints: false,
        isDisplayedBeneficiary: false,
        useOldTicketGenerationType: false,
      },
      methods: {
        updateDisplayedPoints(isDisplayed) {
          this.isDisplayedPoints = isDisplayed
        },
        updateDisplayedBeneficiary(isDisplayed) {
          this.isDisplayedBeneficiary = isDisplayed
        },
        updateUseOldTicketGenerationType(useOldTicketGenerationType) {
          this.useOldTicketGenerationType = useOldTicketGenerationType
        },
      },
      render: h => h(App),
    }).$mount('#app')

    watch(() => store.state.app.settings, () => {
      const isLoggedIn = isUserLoggedIn()
      if (isLoggedIn) {
        // Displayed points and beneficiary
        const { setSettings } = settingsModule()
        setSettings(app)
      }
    })
  }).catch(() => {
    alert('Не удалось подключиться к серверу API')
  })
